import { ChatBubbleLeftIcon } from "@heroicons/react/24/outline";
import { ArrowUpRightIcon } from "@heroicons/react/24/solid";
import clsx from "clsx";
import { format } from "date-fns";
import { Duration } from "luxon";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Badge, Button, Headline, Paragraph, SkeletonLoader } from "@fronterahealth/frontera-ui-components";
import {
  PopoverMenu,
  PopoverMenuItem,
} from "@fronterahealth/frontera-ui-components/PopoverActionMenu/PopoverActionMenu";

import {
  GetMediaTargetPredictionsQuery,
  GoalStatusEnums,
  SortBy,
  useGetMediaTargetPredictionsQuery,
} from "@api/graphql/types-and-hooks";
import { MediaTargetPredictionFeedbackDialog } from "@components/MediaTargetPredictionFeedbackDialog/MediaTargetPredictionFeedbackDialog";
import { UserInitialsView } from "@components/UserInitial/UserInitialsView";
import { PlayAllVideoMomentsButton } from "@pages/ClientProgramming/ProgrammingGoalVideoMoments/PlayAllVideoMomentsButton";
import ProgrammingGoalVideoMomentsFilter from "@pages/ClientProgramming/ProgrammingGoalVideoMoments/ProgrammingGoalVideoMomentsFilter";
import { FeedTooltip } from "@pages/VideoSessionsV2/components/Media/FeedTooltip";
import { useClientProgrammingData } from "@providers/ClientProgrammingProvider";

interface ProgrammingGoalVideoMomentsProps {}

type Session = NonNullable<
  NonNullable<NonNullable<GetMediaTargetPredictionsQuery["getMediaTargetPredictions"]>["edges"][number]>["node"]
>;
type VideoSession = Session["media"]["videoSession"];
type Media = NonNullable<VideoSession>["finalMedia"];

export interface SessionGroup {
  sessions: Session[];
  media: Media;
  videoSession: VideoSession;
}

interface FilterState {
  location?: string;
  targetStatus?: GoalStatusEnums;
  dateRange?: {
    startDate?: Date;
    endDate?: Date;
  };
  sortBy: SortBy;
}

export const ProgrammingGoalVideoMoments: React.FC<ProgrammingGoalVideoMomentsProps> = ({}) => {
  const { selectedGoal, selectedTarget, currentlyPlayingClipID, setCurrentlyPlayingClipID } =
    useClientProgrammingData();
  const [openFeedback, setOpenFeedback] = useState<boolean>(false);
  const [isMomentDialogOpen, setIsMomentDialogOpen] = useState<boolean>(false);

  const [currentSessionMoment, setCurrentSessionMoment] = useState<Session>();
  const [filters, setFilters] = useState<FilterState>({
    sortBy: SortBy.LastUpdated,
  });

  const { data, isLoading } = useGetMediaTargetPredictionsQuery(
    {
      filter: {
        goalIds: [selectedGoal?.id || ""],
        targetId: selectedTarget?.id || "",
        location: filters.location,
        targetStatus: filters.targetStatus,
        startDate: filters.dateRange?.startDate
          ? new Date(new Date(filters.dateRange.startDate).setHours(0, 0, 0, 0)).toISOString()
          : undefined,
        endDate: filters.dateRange?.endDate
          ? new Date(new Date(filters.dateRange.endDate).setHours(23, 59, 59, 999)).toISOString()
          : undefined,
        sortBy: filters.sortBy,
      },
    },
    {
      queryKey: ["getMediaTargetPredictions", selectedGoal, selectedTarget, filters],
      enabled: !!(selectedGoal?.id || selectedTarget?.id),
    },
  );

  const mediaTargetPredictions = data?.getMediaTargetPredictions?.edges || [];

  // Scroll to the currently playing clip
  useEffect(() => {
    if (currentlyPlayingClipID) {
      const element = document.querySelector(`[data-moment-id="${currentlyPlayingClipID}"]`);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }, 100);
      }
    }
  }, [currentlyPlayingClipID]);
  /**
   * group all MediaTargetPredictions by the same Media ID
   * Returns an array to preserve the sort order from the backend
   */
  const groupByMediaId = () => {
    const sessionGroupsMap: Record<string, SessionGroup> = {};
    const orderedMediaIds: string[] = [];

    mediaTargetPredictions?.forEach((prediction) => {
      // Use VideoSession finalMedia because media can be combined
      const videoSession = prediction?.node?.media.videoSession;
      const media = videoSession?.finalMedia;
      const mediaId = media?.id;
      if (!mediaId) return;

      if (!sessionGroupsMap[mediaId]) {
        sessionGroupsMap[mediaId] = {
          sessions: [],
          media: media,
          videoSession: videoSession,
        };
        // Track the order we first see each mediaId
        orderedMediaIds.push(mediaId);
      }

      const mediaTargetPrediction = prediction?.node;
      if (mediaTargetPrediction) {
        sessionGroupsMap[mediaId]["sessions"].push(mediaTargetPrediction);
      }
    });

    // Return array of [mediaId, SessionGroup] tuples in the order they were encountered
    return orderedMediaIds.map((mediaId) => [mediaId, sessionGroupsMap[mediaId]] as [string, SessionGroup]);
  };

  const groupedVideoSessions = groupByMediaId();

  const renderVideoSessionCard = (mediaId: string, sessionGroup: SessionGroup) => {
    // Safety check - make sure we have the required data
    if (!sessionGroup || !sessionGroup.media || !sessionGroup.videoSession) {
      return null;
    }

    const sessionLocation = sessionGroup.videoSession?.therapySession?.locationType;
    const caregiverMeta = sessionGroup.videoSession?.sessioncaregiverSet.edges[0]?.node?.caregiver.caregivermetadata;
    const providerMeta = sessionGroup.videoSession?.sessionproviderSet?.edges[0]?.node?.provider?.providermetadata;
    const sessionRecorder = caregiverMeta || providerMeta;

    return (
      <div key={mediaId} className="flex flex-col w-full bg-white rounded-lg border border-limestone-200">
        <div className="p-3">
          <div className="flex flex-row justify-between">
            <Paragraph size="small" colorType="secondary">
              Video Session
            </Paragraph>
            <div className="flex justify-end gap-2 items-center">
              {sessionLocation && (
                <>
                  <Badge text={sessionLocation} appearance={"error"} />
                  <span className="w-1 h-1 bg-gray-500 rounded-full"></span>
                </>
              )}
              <FeedTooltip text={`${sessionRecorder?.firstName || "N/A"} ${sessionRecorder?.lastName || ""}`}>
                <UserInitialsView
                  className="h-6! w-6! text-[8px]! leading-4 gap-2 "
                  firstName={sessionRecorder?.firstName || ""}
                  lastName={sessionRecorder?.lastName || ""}
                />
              </FeedTooltip>
              <div className="pl-2">
                <Link
                  to={`/video-sessions-v2/${mediaId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex text-limestone-600 hover:text-limestone-800 hover:translate-x-0.5 hover:-translate-y-0.5 transition-all"
                >
                  <ArrowUpRightIcon className="h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>

          <Paragraph size="medium" colorType="primary" className="whitespace-pre-wrap">
            {format(sessionGroup.media.createdDate, "M-d-yyyy @ h:mmaa")}
          </Paragraph>
        </div>

        <div className="border-b border-limestone-100"></div>
        <div className="p-3 w-full">
          {sessionGroup.sessions.map((videoSession, index) => (
            <div key={`${videoSession.id}-${index}`}>{renderSessionMoment(videoSession)}</div>
          ))}
        </div>
      </div>
    );
  };

  const renderSessionMoment = (videoSession: Session) => {
    const isCurrentlyPlaying = currentlyPlayingClipID === videoSession.id;

    const leaveFeedback = () => {
      setCurrentSessionMoment(videoSession);
      setOpenFeedback(true);
    };

    const popoverMenuItems: PopoverMenuItem[] = [
      {
        label: "View Clip in Detail",
        Icon: ArrowUpRightIcon,
      },
      {
        label: "Leave Feedback",
        Icon: ChatBubbleLeftIcon,
        callback: leaveFeedback,
      },
    ];

    return (
      <div data-moment-id={videoSession.id}>
        <div
          className={clsx(
            "flex flex-row pb-4 pt-2 px-2 transition-all duration-300",
            isCurrentlyPlaying && "bg-limestone-100",
          )}
        >
          <img
            src={videoSession.thumbnailUrl || ""}
            alt="No session thumbnail"
            className="h-28 w-28 object-cover rounded-md"
          />
          <div className="flex flex-col pl-6 w-full">
            <div className="flex flex-row justify-between">
              <Paragraph colorType="primary">{videoSession.target?.name || videoSession.goal?.name}</Paragraph>
              <Button
                text={`${Duration.fromObject({ seconds: videoSession.startTime }).toFormat("hh:mm:ss")} - ${Duration.fromObject({ seconds: videoSession.endTime }).toFormat("hh:mm:ss")}`}
                appearance="link"
                onClick={() => {
                  setIsMomentDialogOpen(true);
                  setCurrentlyPlayingClipID(videoSession.id);
                }}
                // className="text-green-900 hover:text-green-700 hover:underline cursor-pointer"
              />
            </div>

            <Paragraph>{videoSession.rationale || ""}</Paragraph>
            <div className="grow"></div>
            <div className="flex flex-row justify-end">
              <PopoverMenu menuItems={popoverMenuItems} />
            </div>
          </div>
        </div>
        <div className="border-b mb-2 border-limestone-100"></div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="pt-2">
        <ProgrammingGoalVideoMomentsFilter setSelectedFilters={setFilters} currentFilters={filters} />
      </div>
      {groupedVideoSessions && groupedVideoSessions.length > 0 && (
        <div className="w-full flex justify-end">
          <PlayAllVideoMomentsButton
            groupedVideoSessions={groupedVideoSessions}
            isOpen={isMomentDialogOpen}
            setIsOpen={setIsMomentDialogOpen}
          />
        </div>
      )}
      <div className="flex flex-col gap-3 items-center w-full overflow-y-auto max-h-[calc(100vh-21rem)] pr-2 mt-2 pb-2">
        {groupedVideoSessions && groupedVideoSessions.length > 0 ? (
          <>{groupedVideoSessions.map(([mediaId, sessionGroup]) => renderVideoSessionCard(mediaId, sessionGroup))}</>
        ) : (
          <div className="flex flex-col items-center gap-2 justify-center w-full h-full">
            {isLoading && (
              <>
                <SkeletonLoader loading={isLoading}>
                  <Paragraph className="h-[calc(65vh-20rem)] w-[calc(100vw-24rem)] mb-2"></Paragraph>
                </SkeletonLoader>
              </>
            )}
            {!isLoading && (
              <div className="flex flex-col items-center pt-12 px-4 justify-center w-full h-full">
                <Headline size="extra-small">No moments found</Headline>
                <Paragraph colorType="secondary">
                  Sorry, we weren't able to detect any moments either from our predictions or from your programming
                  goals and targets.
                </Paragraph>
              </div>
            )}
          </div>
        )}
        <MediaTargetPredictionFeedbackDialog
          open={openFeedback}
          setOpen={setOpenFeedback}
          mediaTargetPrediction={currentSessionMoment}
        />
      </div>
    </div>
  );
};
